import React, { useState, useEffect } from "react";
import { Container, Modal, Button } from "react-bootstrap";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { AuthClientApi } from "../../../../utils/api";
import { toast } from "react-toastify";
import Pagination from "react-js-pagination";

function generateUniqueId() {
  return Date.now().toString() + "-" + Math.floor(Math.random() * 999999);
}

function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default function PendingPlansTab({ setFundData }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [companyTotals, setCompanyTotals] = useState([]);
  const [rejectingPlanId, setRejectingPlanId] = useState(null);
  const [acceptingPlanId, setAcceptingPlanId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [showApproveAllModal, setShowApproveAllModal] = useState(false);
  const [approvingAllPlans, setApprovingAllPlans] = useState(false);

  function isMasterBroker() {
    try {
      const clientData = JSON.parse(localStorage.getItem("client_data"));
      return clientData?.client?.is_master_user === true;
    } catch {
      return false;
    }
  }

  const fetchPlans = async (page = 1) => {
    if (!isMasterBroker()) {
      setLoading(false);
      setError("Only Master Broker can perform this action.");
      toast.error("Only Master Broker can perform this action.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-plans?page=${page}&limit=${perPage}`
      );
      const data = response.data;

      if (data.status && data.planCompanies) {
        // Extract companies from company_totals
        const companiesFromAPI = data.planCompanies.company_totals.map(
          (ct) => ct.short_name
        );
        setCompanies(companiesFromAPI);
        setCompanyTotals(data.planCompanies.company_totals);

        // Set pagination metadata from API response
        setTotalRecords(data.planCompanies.pagination.total || 0);
        setPerPage(data.planCompanies.pagination.limit || 10);
        setCurrentPage(data.planCompanies.pagination.page || page);

        // Transform plans data
        const transformedPlans = data.planCompanies.plans.map((plan) => {
          // Create amounts object for all companies, initialized to 0
          const amounts = {};
          companiesFromAPI.forEach((company) => {
            amounts[company] = { amount: 0 };
          });

          // Fill in actual amounts from plan_companies
          plan.plan_companies.forEach((pc) => {
            const companyName = pc.Company.short_name;
            amounts[companyName] = { amount: parseFloat(pc.allocated_amount) };
          });

          return {
            id: plan.id,
            tradingNumber: plan.customer?.trading_number || "N/A",
            date: new Date(plan.submitted_at).toLocaleDateString("en-GB"),
            amounts: amounts,
            status: plan.status,
          };
        });

        setPlans(transformedPlans);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getClickedPageNo = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchPlans(pageNumber);
  };

  function extractNonZeroAmounts(planData) {
    const amountsObj = {};
    Object.keys(planData.amounts).forEach((company) => {
      if (companies.includes(company)) {
        const val = planData.amounts[company]?.amount || 0;
        if (val > 0) {
          amountsObj[company] = val;
        }
      }
    });
    return amountsObj;
  }

  function addPlanToIndividualBatch(planData) {
    const amountsObj = extractNonZeroAmounts(planData);
    if (Object.keys(amountsObj).length === 0) return;

    const todayStr = new Date().toLocaleDateString("en-GB");
    const deadlineTimestamp = Date.now() + 8 * 60 * 60 * 1000;
    const deadlineDate = new Date(deadlineTimestamp).toISOString();

    const newItem = {
      id: planData.id,
      tradingNumber: planData.tradingNumber,
      date: planData.date,
      amounts: amountsObj,
      status: "Approve",
    };

    setFundData((prevBatches) => {
      const existingIndex = prevBatches.findIndex(
        (b) => b.approvalDate === todayStr && b.batchType === "individual"
      );

      if (existingIndex >= 0) {
        const updated = [...prevBatches];
        const targetBatch = updated[existingIndex];
        targetBatch.items = [...targetBatch.items, newItem];

        const allApproved = targetBatch.items.every(
          (item) => item.status === "Approve"
        );
        if (allApproved) {
          targetBatch.status = "Approve";
        }

        return updated;
      } else {
        const newBatch = {
          id: generateUniqueId(),
          approvalDate: todayStr,
          deadlineDate,
          status: "Approve",
          batchType: "individual",
          items: [newItem],
        };
        return [...prevBatches, newBatch];
      }
    });
  }

  const handleApproveAll = () => {
    if (!isMasterBroker()) {
      setLoading(false);
      setError("Only Master Broker can perform this action.");
      toast.error("Only Master Broker can perform this action.");
      return;
    }

    if (plans.length === 0) return;
    setShowApproveAllModal(true);
  };

  const confirmApproveAll = async () => {
    try {
      setApprovingAllPlans(true);

      // Call the API to approve all plans
      const response = await AuthClientApi.post(
        "/client/blockshares/approve-all-plans"
      );

      // Create a bulk batch with all plans
      createNewBulkBatch(plans);

      // Clear the plans from the current view
      setPlans([]);
      setCompanyTotals([]);
      setCompanies([]);
      setTotalRecords(0);

      toast.success(
        response.data.message || "All plans approved successfully"
      );
      setShowApproveAllModal(false);
    } catch (err) {
      console.error("Error approving all plans:", err);
      toast.error(
        err.response?.data?.message || "Failed to approve all plans"
      );
    } finally {
      setApprovingAllPlans(false);
    }
  };

  const cancelApproveAll = () => {
    setShowApproveAllModal(false);
  };

  function createNewBulkBatch(plansArray) {
    const todayStr = new Date().toLocaleDateString("en-GB");
    const deadlineTimestamp = Date.now() + 8 * 60 * 60 * 1000;
    const deadlineDate = new Date(deadlineTimestamp).toISOString();

    const items = plansArray
      .map((plan) => {
        const amountsObj = extractNonZeroAmounts(plan);
        if (Object.keys(amountsObj).length === 0) return null;
        return {
          id: plan.id,
          tradingNumber: plan.tradingNumber,
          date: plan.date,
          amounts: amountsObj,
          status: "Approved",
        };
      })
      .filter(Boolean);

    if (items.length === 0) return;

    const newBatch = {
      id: generateUniqueId(),
      approvalDate: todayStr,
      deadlineDate,
      status: "Approve",
      batchType: "bulkAll",
      items,
    };

    setFundData((prevBatches) => [...prevBatches, newBatch]);
  }

  const handleApprove = async (planData) => {
    if (!isMasterBroker()) {
      setLoading(false);
      setError("Only Master Broker can perform this action.");
      toast.error("Only Master Broker can perform this action.");
      return;
    }

    try {
      setAcceptingPlanId(planData.id); // Set loading state
      const response = await AuthClientApi.post(
        `/client/blockshares/accept-plan/${planData.id}`
      );
      addPlanToIndividualBatch(planData);
      setPlans((prev) => prev.filter((p) => p.id !== planData.id));
      toast.success(response.data.message || "Plan accepted successfully");
    } catch (err) {
      console.error("Error accepting plan:", err);
      toast.error(err.response?.data?.message || "Failed to accept plan");
    } finally {
      setAcceptingPlanId(null); // Clear loading state
    }
  };

  const handleReject = async (planData) => {
    if (!isMasterBroker()) {
      setLoading(false);
      setError("Only Master Broker can perform this action.");
      toast.error("Only Master Broker can perform this action.");
      return;
    }

    try {
      setRejectingPlanId(planData.id); // Set loading state
      const response = await AuthClientApi.put(
        `/client/blockshares/reject-plan/${planData.id}`
      );

      // Remove from local state after successful API call
      setPlans((prev) => prev.filter((p) => p.id !== planData.id));

      // Show success toast
      toast.success(response.data.message || "Plan rejected successfully");
    } catch (err) {
      console.error("Error rejecting plan:", err);
      // Show error toast
      toast.error(err.response?.data?.message || "Failed to reject plan");
    } finally {
      setRejectingPlanId(null); // Clear loading state
    }
  };

  const handleDeleteCompany = (company) => {
    // Find the company object to get its ID
    const companyObj = companyTotals.find((ct) => ct.short_name === company);
    if (!companyObj) {
      toast.error("Company not found");
      return;
    }

    setCompanyToDelete({ name: company, id: companyObj.company_id });
    setShowDeleteModal(true);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;

    try {
      setDeletingCompany(true);

      // Get all plan IDs from current visible plans
      const planIds = plans.map((plan) => plan.id);

      const requestBody = {
        company_id: companyToDelete.id,
        plan_ids: planIds,
      };

      // Call the API
      const response = await AuthClientApi.post(
        "/client/blockshares/plan/remove-companies",
        requestBody
      );

      // Remove the company from the list entirely
      setCompanies((prevCompanies) =>
        prevCompanies.filter((company) => company !== companyToDelete.name)
      );
      setCompanyTotals((prevTotals) =>
        prevTotals.filter((ct) => ct.short_name !== companyToDelete.name)
      );

      toast.success(
        response.data.message || "Company deleted successfully"
      );
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    } catch (err) {
      console.error("Error deleting company:", err);
      toast.error(
        err.response?.data?.message || "Failed to delete company"
      );
    } finally {
      setDeletingCompany(false);
    }
  };

  const cancelDeleteCompany = () => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  };

  const getTotalForCompany = (company) => {
    // Use API company totals if available, otherwise calculate from plans
    const companyTotal = companyTotals.find((ct) => ct.short_name === company);
    if (companyTotal) {
      return companyTotal.total_allocated_amount;
    }

    return plans.reduce(
      (sum, plan) => sum + (plan.amounts[company]?.amount || 0),
      0
    );
  };

  const getGrandTotal = () => {
    // Calculate from company totals for better performance
    return companyTotals.reduce(
      (grandTotal, ct) => grandTotal + ct.total_allocated_amount,
      0
    );
  };

  return (
    <>
      <Container fluid className="plans-container fade-in">
        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            Error loading plans: {error}
            <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={fetchPlans}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="plans-summary-row">
              <div className="plans-summary-text">
                You have <strong>{totalRecords}</strong> plans pending
                approval.
              </div>
              <button
                className="btn btn-approve-all"
                onClick={handleApproveAll}
                disabled={plans.length === 0}
              >
                <CheckIcon style={{ marginBottom: "2px" }} /> Approve All
              </button>
            </div>

            <div className="table-responsive custom-scroll">
              <table className="table table-bordered plans-table">
                <thead>
                  <tr className="plans-header-row">
                    <th className="sticky-col">Acc #</th>
                    <th className="sticky-col-2">Date</th>
                    {companies.map((company) => (
                      <th key={company}>
                        <div className="company-col">
                          {company}
                          <button
                            className="btn-transfer-bal"
                            onClick={() => handleDeleteCompany(company)}
                          >
                            Delete
                          </button>
                        </div>
                      </th>
                    ))}
                    <th className="sticky-col-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2 + companies.length + 1}
                        style={{
                          textAlign: "center",
                          padding: "40px 20px",
                          color: "#666",
                          fontSize: "1.1rem",
                        }}
                      >
                        No plans pending approval.
                      </td>
                    </tr>
                  ) : (
                    plans.map((plan, index) => (
                      <tr key={index}>
                        <td className="sticky-col">{plan.tradingNumber}</td>
                        <td className="sticky-col-2">{plan.date}</td>
                        {companies.map((company) => {
                          const amountInfo = plan.amounts[company];
                          const amount = amountInfo ? amountInfo.amount : 0;
                          return (
                            <td key={company}>
                              {amount > 0 ? formatNumber(amount) : 0}
                            </td>
                          );
                        })}
                        <td className="sticky-col-right action-cell">
                          <button
                            className="btn-approve-small"
                            onClick={() => handleApprove(plan)}
                            disabled={acceptingPlanId === plan.id}
                            style={{
                              position: "relative",
                              padding: "0px !important",
                            }}
                          >
                            {acceptingPlanId === plan.id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <>
                                <CheckIcon style={{ marginBottom: "2px" }} />{" "}
                                Approve
                              </>
                            )}
                          </button>
                          <button
                            className="btn-reject-small"
                            onClick={() => handleReject(plan)}
                            disabled={rejectingPlanId === plan.id}
                            style={{ position: "relative" }}
                          >
                            {rejectingPlanId === plan.id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <>
                                <CloseIcon style={{ marginBottom: "2px" }} />{" "}
                                Reject
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {plans.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="sticky-col">{plans.length}</td>
                      <td className="sticky-col-2">Total</td>
                      {companies.map((company) => {
                        const total = getTotalForCompany(company);
                        return (
                          <td key={company}>
                            {total > 0 ? formatNumber(total) : 0}
                          </td>
                        );
                      })}
                      <td className="sticky-col-right">
                        {formatNumber(getGrandTotal())}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </Container>

      {/* Pagination */}
      {!loading && !error && plans.length > 0 && (
        <div className="pagination-wrapper-right">
          <Pagination
            activePage={currentPage}
            prevPageText="prev"
            nextPageText="next"
            itemClass="page-item"
            linkClass="page-link"
            pageRangeDisplayed={5}
            itemsCountPerPage={perPage}
            totalItemsCount={totalRecords}
            onChange={getClickedPageNo}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={cancelDeleteCompany} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{companyToDelete?.name}</strong> from the currently visible{" "}
          {plans.length} plan(s)?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={cancelDeleteCompany}
            disabled={deletingCompany}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteCompany}
            disabled={deletingCompany}
          >
            {deletingCompany ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve All Confirmation Modal */}
      <Modal show={showApproveAllModal} onHide={cancelApproveAll} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Approve All</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to approve all <strong>{totalRecords}</strong>{" "}
          pending plan(s)? This will approve all the plans.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={cancelApproveAll}
            disabled={approvingAllPlans}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmApproveAll}
            disabled={approvingAllPlans}
          >
            {approvingAllPlans ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Approving...
              </>
            ) : (
              "Approve All"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
